/**
 * sdk-surface.ts — static knowledge of the SDK surface for CLI introspection.
 *
 * Phase CI (2026-05-15). Pulled from:
 *   /sdk/api/         — 138 endpoints across 31 groups
 *   /sdk/protocols/   — 236 protocols across 9 themes + 50 domains
 *   /sdk/operators/   — 1,500+ operators across 8 families, 67 domains
 *   /sdk/sdks/kernel-skill/ — 3 endpoints (prompt, skill.md, skill JSON)
 *
 * Bundle cost: ~3 KB of strings. Pays for itself by letting `api groups`,
 * `protocols themes`, `families list quantum`, `skill install claude-code`
 * answer instantly without an extra round-trip.
 */

export interface ApiGroup {
  name: string;
  slug: string;        // SDK URL slug
  count: number;
  blurb: string;
}

/** The 31 API groups. Counts from /sdk/api/ at 2026-05-15. */
export const API_GROUPS: ApiGroup[] = [
  { name: "Entangled state",             slug: "chain",             count: 15, blurb: "State-machine spin-up + entangled-state HTTP surface (PoHC, explore, SSE)." },
  { name: "Contracts",         slug: "contracts",         count: 6,  blurb: "JSON state-machine contracts (deploy, transition, audit, verify)." },
  { name: "Contract Templates", slug: "contract-templates", count: 4, blurb: "18 ready-to-deploy contract templates." },
  { name: "Tally Protocol",    slug: "tally-protocol",    count: 14, blurb: "Tally token economy: balance, transfer, burn, mint, ledger, transparency." },
  { name: "Tally",       slug: "tally-chain",       count: 4,  blurb: "Per-state-machine token surface (supply, list, detail, verify)." },
  { name: "PoHC Protocol",     slug: "pohc-protocol",     count: 8,  blurb: "Proof of Harmonic Convergence layer + sybil/reputation." },
  { name: "Embed",             slug: "embed",             count: 3,  blurb: "Universal zeq-embed.js tracker — ingest, recent, pixel." },
  { name: "Auth",              slug: "auth",              count: 1,  blurb: "Session sign-in, token exchange, refresh." },
  { name: "Dev",               slug: "dev",               count: 3,  blurb: "Developer dashboard + integration tooling." },
  { name: "Download",          slug: "download",          count: 2,  blurb: "Artifact + SDK release delivery." },
  { name: "Explorer",          slug: "explorer",          count: 4,  blurb: "Interactive protocol/operator explorer." },
  { name: "Framework",         slug: "framework",         count: 11, blurb: "Top-level framework metadata + descriptor." },
  { name: "Genesis",           slug: "genesis",           count: 2,  blurb: "Genesis key issuance + verification." },
  { name: "Health",            slug: "health",            count: 2,  blurb: "Liveness + readiness probes." },
  { name: "HITE",              slug: "hite",              count: 3,  blurb: "HITE encryption key lifecycle + envelope ops." },
  { name: "Kernel",            slug: "kernel",            count: 4,  blurb: "Kernel introspection + HulyaPulse state." },
  { name: "Keys",              slug: "keys",              count: 2,  blurb: "API-key management surface." },
  { name: "LLM Gateway",       slug: "llm-gateway",       count: 4,  blurb: "Equation-grounded LLM proxy + tool use." },
  { name: "Marketing",         slug: "marketing",         count: 5,  blurb: "Signup, waitlist, newsletter." },
  { name: "MCP",               slug: "mcp",               count: 2,  blurb: "Model Context Protocol server bindings." },
  { name: "Node",              slug: "node",              count: 4,  blurb: "Mesh-node registration + status." },
  { name: "Operators",         slug: "operators",         count: 2,  blurb: "Operator registry read + lookup." },
  { name: "Playground",        slug: "playground",        count: 1,  blurb: "Public no-auth compute playground." },
  { name: "PLNB",              slug: "plnb",              count: 1,  blurb: "Public ledger / non-block surface." },
  { name: "PoHC",              slug: "pohc",              count: 3,  blurb: "Proof-of-Harmonic-Convergence reads." },
  { name: "Protocol",          slug: "protocol",          count: 3,  blurb: "Generic protocol reflection." },
  { name: "Portal",            slug: "portal",            count: 2,  blurb: "Per-machine portal surface." },
  { name: "Protocols",         slug: "protocols",         count: 4,  blurb: "Protocols catalogue read API." },
  { name: "Skills",            slug: "skills",            count: 5,  blurb: "Skill synthesis + listing." },
  { name: "Store",             slug: "store",             count: 3,  blurb: "App-store metadata for surfaces." },
  { name: "Trial",             slug: "trial",             count: 2,  blurb: "Free-trial wiring." },
];

export const API_TOTAL_ENDPOINTS = 138;
export const API_TOTAL_GROUPS = 31;

export interface ProtocolTheme {
  index: number;
  name: string;
  covers: string;
  anchors: string[];
}

/** The 9 thematic chapters from /sdk/protocols/. */
export const PROTOCOL_THEMES: ProtocolTheme[] = [
  { index: 1, name: "Time & Coordination",         covers: "Zeqond, HulyaPulse, ZTB1 bridge, rendezvous, sync",                anchors: ["KO42", "TM1", "ZTB1"] },
  { index: 2, name: "Identity & Access",           covers: "Sibling tether, challenge-response, federated ID",                  anchors: ["KO42", "ZEQ-TETHER-003", "CS87"] },
  { index: 3, name: "Key & Custody",               covers: "KDF, rotation, vault, BYOK",                                        anchors: ["KO42", "CS87", "ZEQ-POCKET-001"] },
  { index: 4, name: "Encryption",                  covers: "AEAD envelopes, time-locks, TESC frames",                           anchors: ["KO42", "CS87", "TM1"] },
  { index: 5, name: "Transport & Messaging",       covers: "Mail, message, mesh, streams",                                       anchors: ["KO42", "CS47", "CS43"] },
  { index: 6, name: "Computation & Compile",       covers: "Operator composition, Master Equation, Functional Equation",       anchors: ["KO42", "CS43", "CS46"] },
  { index: 7, name: "Consensus & Ledger",          covers: "Zeqond-anchored blocks, truth DAG",                                  anchors: ["KO42", "CS87", "ZEQ-TETHER-003"] },
  { index: 8, name: "Measurement & Observability", covers: "Pulse metrics, attestation records, provenance logs",                anchors: ["KO42", "CS47", "QM10"] },
  { index: 9, name: "Governance & Policy",         covers: "Admission, deprecation, quorum, lifecycle",                          anchors: ["KO42", "ZEQ-TETHER-003", "CS47"] },
];

/** Domain coverage list (~50 domains) from the protocols index. */
export const PROTOCOL_DOMAINS: string[] = [
  "Agriculture", "Audio", "Automotive", "Aviation", "Biology", "Blockchain",
  "Climate", "Communications", "Compute", "Core", "Data", "Defense",
  "Ecosystem", "Education", "Emergency", "Energy", "Finance", "Forensics",
  "Gaming", "Geoscience", "Hardware", "Healthcare", "Identity", "Infrastructure",
  "IoT", "Manufacturing", "Maritime", "Materials", "Medical Devices",
  "Medical Imaging", "Network", "Nuclear", "Nutrition", "Optics", "Pharma",
  "Quantum", "Robotics", "Security", "Space", "Telecom", "Water", "Weather",
];

export const PROTOCOL_TOTAL = 236;

export interface OperatorFamily {
  name: string;
  slug: string;
  range: string;
  covers: string;
}

/** The 8 operator families from /sdk/operators/. */
export const OPERATOR_FAMILIES: OperatorFamily[] = [
  { name: "Kinematic Spectrum",   slug: "kinematic",        range: "KO42 + the 42+ core",                  covers: "Bootstrap surface — five tiers (QM · NM · GR · CS · Awareness)." },
  { name: "Quantum Mechanics",    slug: "quantum-mechanics", range: "QM1 – QM17",                          covers: "Wave mechanics, uncertainty, superposition, Dirac, statistics." },
  { name: "Newtonian Mechanics",  slug: "newtonian-mechanics", range: "NM18 – NM30",                       covers: "Forces, momentum, gravity, oscillation, rigid body." },
  { name: "General Relativity",   slug: "general-relativity", range: "GR31 – GR41",                        covers: "Metric, geodesics, redshift, cosmology, Friedmann." },
  { name: "Computer Science",     slug: "computer-science",   range: "CS43 – CS87",                        covers: "Complexity, entropy, parallelism, Kolmogorov, Big-O." },
  { name: "Awareness",            slug: "awareness",          range: "ON0 · QL1 · TM1 · TX · XI1 · LZ1 · CHI95 · PSI96 · MK1 · VX · ZEQ00 · ZEQ000 · ZEQ-PROTECT · ZEQ-TETHER · ZEQ-POCKET", covers: "Observation, intent, protection, tethering, pocket geometry." },
  { name: "Hulyas Forensics",     slug: "hulyas-forensics",   range: "HF1 – HF20 + composite",             covers: "Source verification, manipulation detection, Bayesian inference, composite scoring." },
  { name: "Agentic Operations",   slug: "agentic-operations", range: "AGO1 – AGO5 + VX-variants + ZRO-B",  covers: "Awareness gradients, growth dynamics, vocal expression, qualia mapping, bridge coupling." },
  { name: "Zeq Resonance",        slug: "zeq-resonance",      range: "ZRO00 · ZRO93 – ZRO148 · ZRO149 · ZRO201 – ZRO210 · RHY1 – RHY4", covers: "Zeq Resonant core, meta-architect, qualia synthesis, temporal anchoring, sibling bond." },
];

export const OPERATOR_TOTAL = 1942;
export const OPERATOR_DOMAINS = 67;

export interface KernelEndpoint {
  path: string;
  mime: string;
  use: string;
}

/** The 3 canonical Kernel Skill endpoints from /sdk/sdks/kernel-skill/. */
export const KERNEL_ENDPOINTS: KernelEndpoint[] = [
  { path: "/api/kernel/prompt",   mime: "text/plain",       use: "Raw boot sequence — paste into any system-prompt field." },
  { path: "/api/kernel/skill.md", mime: "text/markdown",    use: "Full SKILL.md with YAML frontmatter — drop into a skills directory." },
  { path: "/api/kernel/skill",    mime: "application/json", use: "Manifest with version, sha256, per-platform install steps." },
];

/** Install snippets for the 4 supported clients. */
export const KERNEL_INSTALLERS: Record<string, { label: string; snippet: string }> = {
  "claude-code": {
    label: "Claude Code",
    snippet:
      "mkdir -p ~/.claude/skills/zeq-os-mi-kernel\n" +
      "curl -sL https://www.zeq.dev/api/kernel/skill.md \\\n" +
      "  -o ~/.claude/skills/zeq-os-mi-kernel/SKILL.md\n" +
      "# Restart Claude Code or reload skills",
  },
  cowork: {
    label: "Cowork",
    snippet:
      "# In a fresh plugin, paste the markdown into the plugin's SKILL.md:\n" +
      "curl -sL https://www.zeq.dev/api/kernel/skill.md",
  },
  cursor: {
    label: "Cursor",
    snippet:
      "mkdir -p .cursor/rules\n" +
      "curl -sL https://www.zeq.dev/api/kernel/prompt \\\n" +
      "  -o .cursor/rules/zeq-kernel.md",
  },
  openai: {
    label: "OpenAI Assistants",
    snippet:
      "curl -sL https://www.zeq.dev/api/kernel/prompt\n" +
      '# Paste the response into the "Instructions" field on platform.openai.com',
  },
};

/** Map of doc topic → SDK URL — expanded set used by `docs <topic>`. */
export const DOC_MAP: Record<string, string> = {
  // Existing core
  operators:      "/sdk/operators/",
  contracts:      "/sdk/learn/contracts",
  queries:        "/sdk/learn/contracts",
  hite:           "/sdk/learn/hite",
  auth:           "/sdk/learn/auth-v3",
  economy:        "/sdk/learn/tally-tokens",
  credits:        "/sdk/learn/tally-tokens",
  agents:         "/sdk/learn/agents",
  byok:           "/sdk/learn/byok",
  pulse:          "/sdk/learn/pulse-orb",
  cli:            "/sdk/how-to/use-cli",
  skills:         "/sdk/learn/skill-synthesis",
  transparency:   "/sdk/api/transparency",
  // Phase CI additions
  api:            "/sdk/api/",
  protocols:      "/sdk/protocols/",
  "kernel-skill": "/sdk/sdks/kernel-skill/",
  kernel:         "/sdk/sdks/kernel-skill/",
  mcp:            "/sdk/sdks/mcp-server/",
  sdks:           "/sdk/sdks/",
  families:       "/sdk/operators/",
  ship:           "/sdk/ship/",
  build:          "/sdk/build/",
  learn:          "/sdk/learn/",
  start:          "/sdk/start/",
};
